// From forge.ts
export interface ComponentState {
	[hookIndex: number]: any;
}

export interface RenderState {
	[componentKey: string]: ComponentState;
}

export interface Handler {
	componentKey: string;
	prop: string;
}

export type ForgeProps = { [key: string]: any };

export interface ForgeDoc {
	children: ForgeDoc[];
	key?: string;
	props?: ForgeProps;
	type: string;
	reconciliationCount?: number;
	// Number aligns with the current major version of forge-react.
	// This is used to handle which set of UI Kit 2 component versions we will render.
	// For example, version 10 of @forge/react will contain code-generated components based off of Atlassian Design System components
	// These components contain new behaviour and accept different props compared to their version 9 counterparts.
	// Version 10 of @forge/react will only render these new components. Version 9 and below will continue to render the older implementations
	forgeReactMajorVersion?: number;
	// caching the change detection result between the previous and current props
	// so that we avoid re-computing the deep comparison of props on the nested
	// element unnecessarily.
	hasChanged?: boolean;
}

export type ForgeElement<P = Record<string, any>> = PrimitiveElement<P> | FunctionElement<P>;

export interface PrimitiveElement<P = Record<string, any>> {
	type: string;
	// After @types/react@17.0.65, `key` is now only string | null
	// (see: https://github.com/DefinitelyTyped/DefinitelyTyped/pull/66735)
	// Previously it was string | number | null
	// Because @forge/ui and AFE use different versions of @types/react,
	// we need to use `any` here to avoid type errors on both sides.
	key: any;
	props: P & { children: ForgeNode[] };
}

export interface FunctionElement<P = Record<string, any>> {
	type: (props: P) => ForgeElement;
	key: any;
	props: P & { children: ForgeNode[] };
}

export const isForgeElement = (auxNode: ForgeNode): auxNode is ForgeElement => {
	return auxNode !== null && typeof auxNode !== 'boolean' && auxNode !== undefined;
};

export type ForgeNode = ForgeElement | null | boolean | undefined;

export type ForgeChildren<T = ForgeNode> = T | (T | T[])[];

export interface PlatformContext {
	type: string;
}

export interface ExtensionContext {
	type: string;
	[key: string]: any;
}

export type CapabilitySetType = 'capabilityStandard' | 'capabilityAdvanced';

export enum EcosystemLicenseMode {
	USER_ACCESS = 'USER_ACCESS',
}
export interface LicenseDetails {
	active: boolean;
	billingPeriod: string;
	capabilitySet: CapabilitySetType;
	ccpEntitlementId: string;
	ccpEntitlementSlug: string;
	isEvaluation: boolean;
	subscriptionEndDate: string | null;
	supportEntitlementNumber: string | null;
	trialEndDate: string | null;
	type: string;
	modes: [EcosystemLicenseMode] | null;
}

export interface UserAccessDetails {
	enabled: boolean;
	hasAccess: boolean;
}

export type AccountType = 'licensed' | 'unlicensed' | 'customer' | 'anonymous';

export interface ProductContext {
	accountId?: string;
	accountType?: AccountType;
	cloudId?: string;
	contentId?: string;
	localId?: string;
	spaceKey?: string;
	spaceId?: string;
	installContext?: string;
	platformContext?: PlatformContext;
	isConfig?: boolean;
	extensionContext?: ExtensionContext;
	license?: LicenseDetails;
	moduleKey?: string;
	siteUrl?: string;
	appId?: string;
	environmentId?: string;
	environmentType?: string;
	workspaceId?: string;
}

export interface ExtensionConfiguration {
	[key: string]: any;
}

export interface User {
	accountId: string;
}

export type CustomFieldValue = string | number | User | null;

export interface JiraContext extends PlatformContext {
	type: 'jira';
	issueId?: number;
	issueKey?: string;
	issueType?: string;
	projectKey?: string;
	projectId?: string;
	projectType?: string;
}

export const isJiraContext = (context: PlatformContext): context is JiraContext =>
	context.type === 'jira';

export interface IssuePanelExtensionContext extends ExtensionContext {
	type: 'issuePanel';
	isNewToIssue: boolean;
}

export const isIssuePanelExtensionContext = (
	extensionContext: ExtensionContext,
): extensionContext is IssuePanelExtensionContext => extensionContext.type === 'issuePanel';

export interface CustomFieldContextConfigExtensionContext extends ExtensionContext {
	type: 'contextConfig';
	fieldId: string;
	fieldType: string;
	contextId: number;
	configuration?: any;
	schema?: { [key: string]: any };
}

export const isCustomFieldContextConfigExtensionContext = (
	extensionContext: ExtensionContext,
): extensionContext is CustomFieldContextConfigExtensionContext =>
	extensionContext.type === 'contextConfig';

export interface CustomFieldExtensionContext extends ExtensionContext {
	type: 'customField' | 'customFieldType';
	fieldValue: CustomFieldValue;
	fieldId: string;
	fieldType: string;
	isInline?: boolean;
}

export const isCustomFieldExtensionContext = (
	extensionContext: ExtensionContext,
): extensionContext is CustomFieldExtensionContext =>
	extensionContext.type === 'customField' || extensionContext.type === 'customFieldType';

export interface ContextMenuExtensionContext extends ExtensionContext {
	type: 'contextMenu';
	selectedText: string;
}

export interface ContentActionExtensionContext extends ExtensionContext {
	type: 'contentAction';
}

export const isContextMenuExtensionContext = (
	extensionContext: ExtensionContext,
): extensionContext is ContextMenuExtensionContext => extensionContext.type === 'contextMenu';

export const isContentActionExtensionContext = (
	extensionContext: ExtensionContext,
): extensionContext is ContentActionExtensionContext => extensionContext.type === 'contentAction';

export interface DashboardGadgetExtensionContext extends ExtensionContext {
	type: 'dashboardGadget';
	gadgetConfiguration: Record<string, any>;
}

export const isDashboardGadgetExtensionContext = (
	extensionContext: ExtensionContext,
): extensionContext is DashboardGadgetExtensionContext =>
	extensionContext.type === 'dashboardGadget';

export enum CompassContextTypes {
	AdminPage = 'compass:adminPage',
	ComponentPage = 'compass:componentPage',
	TeamPage = 'compass:teamPage',
	ComponentImporter = 'compass:componentImporter',
}

export enum RenderingLocations {
	AppConfigurationPage = 'appConfigurationPage',
	OnboardingFlow = 'onboardingFlow',
}

export interface CompassComponentPageExtensionContext extends ExtensionContext {
	type: CompassContextTypes.ComponentPage;
	componentId: string;
}

export interface CompassAdminPageExtensionContext extends ExtensionContext {
	type: CompassContextTypes.AdminPage;
	url: string;
	renderingLocation: string;
}

export interface CompassComponentImporterExtensionContext extends ExtensionContext {
	type: CompassContextTypes.ComponentImporter;
	renderingLocation: string;
}

export interface CompassTeamPageExtensionContext extends ExtensionContext {
	type: CompassContextTypes.TeamPage;
	teamId: string;
}
